import { useCallback, useEffect, useRef, useState } from "react";
import api, { formatApiError } from "../lib/api";
import { uploadMedia, mediaUrl, MAX_MEDIA_BYTES } from "../lib/mediaUpload";
import { toast } from "sonner";
import {
  UploadCloud, File as FileIcon, FileText, FileSpreadsheet, FileArchive,
  Image as ImageIcon, Film, Music, Trash2, Download, ExternalLink,
  Globe, Lock, Loader2,
} from "lucide-react";

function humanSize(n) {
  if (!n && n !== 0) return "—";
  if (n < 1024) return `${n} B`;
  if (n < 1024 * 1024) return `${(n / 1024).toFixed(1)} KB`;
  return `${(n / (1024 * 1024)).toFixed(1)} MB`;
}

function iconFor(ct = "") {
  const t = ct.toLowerCase();
  if (t.startsWith("image/")) return ImageIcon;
  if (t.startsWith("video/")) return Film;
  if (t.startsWith("audio/")) return Music;
  if (t.includes("pdf")) return FileText;
  if (t.includes("sheet") || t.includes("excel") || t.includes("csv")) return FileSpreadsheet;
  if (t.includes("zip") || t.includes("compressed") || t.includes("tar")) return FileArchive;
  if (t.includes("text") || t.includes("word") || t.includes("document")) return FileText;
  return FileIcon;
}

export function MediaManager({
  contextType = "library",
  entityId = null,
  title = "Files & Media",
  subtitle = null,
  defaultPublic = false,
  compact = false,
}) {
  const [items, setItems] = useState([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);
  const [uploading, setUploading] = useState(false);
  const [progress, setProgress] = useState(0);
  const [makePublic, setMakePublic] = useState(defaultPublic);
  const [dragOver, setDragOver] = useState(false);
  const inputRef = useRef(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams({ context_type: contextType, limit: "200" });
      if (entityId) params.set("entity_id", entityId);
      const { data } = await api.get(`/media?${params.toString()}`);
      setItems(data.items || []);
      setTotal(data.total || 0);
    } catch (e) {
      toast.error("Failed to load files", { description: formatApiError(e?.response?.data?.detail) });
    } finally {
      setLoading(false);
    }
  }, [contextType, entityId]);

  useEffect(() => { load(); }, [load]);

  const handleFiles = useCallback(async (fileList) => {
    const files = Array.from(fileList || []);
    if (!files.length) return;
    setUploading(true);
    let ok = 0;
    for (const file of files) {
      if (file.size > MAX_MEDIA_BYTES) {
        toast.error(`${file.name} is too large (20 MB max)`);
        continue;
      }
      try {
        setProgress(0);
        await uploadMedia(file, {
          contextType, entityId, isPublic: makePublic,
          onProgress: setProgress,
        });
        ok += 1;
      } catch (e) {
        toast.error(`Upload failed: ${file.name}`, { description: formatApiError(e?.response?.data?.detail || e.message) });
      }
    }
    setUploading(false);
    setProgress(0);
    if (ok) { toast.success(`${ok} file${ok === 1 ? "" : "s"} uploaded`); await load(); }
  }, [contextType, entityId, makePublic, load]);

  const onDrop = useCallback((e) => {
    e.preventDefault();
    setDragOver(false);
    handleFiles(e.dataTransfer.files);
  }, [handleFiles]);

  const togglePublic = async (file) => {
    try {
      const { data } = await api.patch(`/media/${file.id}`, { is_public: !file.is_public });
      setItems((prev) => prev.map((f) => (f.id === file.id ? data : f)));
      toast.success(data.is_public ? "File is now public" : "File is now private");
    } catch (e) {
      toast.error("Update failed", { description: formatApiError(e?.response?.data?.detail) });
    }
  };

  const remove = async (file) => {
    if (!window.confirm(`Delete "${file.filename}"? This cannot be undone.`)) return;
    try {
      await api.delete(`/media/${file.id}`);
      setItems((prev) => prev.filter((f) => f.id !== file.id));
      setTotal((t) => Math.max(0, t - 1));
      toast.success("File deleted");
    } catch (e) {
      toast.error("Delete failed", { description: formatApiError(e?.response?.data?.detail) });
    }
  };

  if (compact) {
    return (
      <CompactMedia
        contextType={contextType} items={items} loading={loading} uploading={uploading} progress={progress}
        dragOver={dragOver} setDragOver={setDragOver} onDrop={onDrop} inputRef={inputRef}
        handleFiles={handleFiles} togglePublic={togglePublic} remove={remove}
      />
    );
  }

  return (
    <div data-testid={`media-manager-${contextType}`}>
      <div className="flex flex-wrap items-end justify-between gap-3 mb-3">
        <div>
          <h3 className="text-sm font-bold uppercase tracking-widest text-[#C8A46B] font-mono flex items-center gap-2">
            <UploadCloud className="w-4 h-4" /> {title}
            <span className="text-slate-500 normal-case tracking-normal font-sans" data-testid="media-count">
              ({total})
            </span>
          </h3>
          {subtitle && <p className="text-xs text-slate-400 mt-1">{subtitle}</p>}
        </div>
        <label className="flex items-center gap-2 text-xs text-slate-300 cursor-pointer select-none" data-testid="media-public-toggle">
          <input
            type="checkbox"
            checked={makePublic}
            onChange={(e) => setMakePublic(e.target.checked)}
            className="accent-emerald-500"
          />
          Upload as public link
        </label>
      </div>

      {/* Drop zone */}
      <div
        onDragOver={(e) => { e.preventDefault(); setDragOver(true); }}
        onDragLeave={() => setDragOver(false)}
        onDrop={onDrop}
        onClick={() => !uploading && inputRef.current?.click()}
        className={`rounded-xl border-2 border-dashed transition-colors cursor-pointer px-4 py-6 text-center ${
          dragOver ? "border-[#C8A46B] bg-[#C8A46B]/5" : "border-white/12 bg-white/[0.02] hover:border-white/25"
        }`}
        data-testid="media-dropzone"
      >
        <input
          ref={inputRef}
          type="file"
          multiple
          className="hidden"
          onChange={(e) => { handleFiles(e.target.files); e.target.value = ""; }}
          data-testid="media-file-input"
        />
        {uploading ? (
          <div className="flex flex-col items-center gap-2 text-slate-300">
            <Loader2 className="w-6 h-6 animate-spin text-[#C8A46B]" />
            <div className="text-sm">Uploading… {progress}%</div>
            <div className="w-48 h-1.5 rounded-full bg-white/10 overflow-hidden">
              <div className="h-full bg-[#C8A46B] transition-all" style={{ width: `${progress}%` }} />
            </div>
          </div>
        ) : (
          <div className="flex flex-col items-center gap-1 text-slate-400">
            <UploadCloud className="w-6 h-6 text-slate-500" />
            <div className="text-sm text-slate-300">Drop files here or <span className="text-[#C8A46B]">browse</span></div>
            <div className="text-[11px] text-slate-500">Any file type · up to 20 MB each</div>
          </div>
        )}
      </div>

      {/* File list */}
      <div className="mt-3 space-y-2" data-testid="media-list">
        {loading && items.length === 0 && (
          <div className="text-center text-slate-500 text-sm py-6"><Loader2 className="w-4 h-4 animate-spin inline mr-2" />Loading…</div>
        )}
        {!loading && items.length === 0 && (
          <div className="text-center text-slate-500 text-sm py-6" data-testid="media-empty">No files yet.</div>
        )}
        {items.map((f) => {
          const Icon = iconFor(f.content_type);
          const isImage = (f.content_type || "").startsWith("image/");
          return (
            <div
              key={f.id}
              className="flex items-center gap-3 rounded-lg border border-white/8 bg-white/[0.02] px-3 py-2 hover:bg-white/[0.04]"
              data-testid={`media-item-${f.id}`}
            >
              <div className="w-10 h-10 rounded-md bg-white/5 border border-white/10 flex items-center justify-center overflow-hidden shrink-0">
                {isImage ? (
                  <img src={mediaUrl(f)} alt={f.filename} className="w-full h-full object-cover" />
                ) : (
                  <Icon className="w-5 h-5 text-slate-400" />
                )}
              </div>
              <div className="min-w-0 flex-1">
                <div className="text-sm text-slate-100 truncate" title={f.filename}>{f.filename}</div>
                <div className="text-[11px] text-slate-500 truncate">
                  {humanSize(f.size)} · {f.uploaded_by_name || "—"}
                </div>
              </div>
              <button
                onClick={() => togglePublic(f)}
                className={`inline-flex items-center gap-1 px-2 py-1 rounded text-[11px] border ${
                  f.is_public
                    ? "text-emerald-300 border-emerald-500/30 bg-emerald-500/10"
                    : "text-slate-300 border-white/10 bg-white/5"
                }`}
                title={f.is_public ? "Public — click to make private" : "Private — click to make public"}
                data-testid={`media-toggle-${f.id}`}
              >
                {f.is_public ? <Globe className="w-3 h-3" /> : <Lock className="w-3 h-3" />}
                {f.is_public ? "Public" : "Private"}
              </button>
              <a
                href={mediaUrl(f)}
                target="_blank"
                rel="noreferrer"
                className="p-1.5 rounded text-slate-400 hover:text-white hover:bg-white/5"
                title="Open / preview"
                data-testid={`media-open-${f.id}`}
              >
                <ExternalLink className="w-4 h-4" />
              </a>
              <a
                href={mediaUrl(f, { download: true })}
                className="p-1.5 rounded text-slate-400 hover:text-white hover:bg-white/5"
                title="Download"
                data-testid={`media-download-${f.id}`}
              >
                <Download className="w-4 h-4" />
              </a>
              <button
                onClick={() => remove(f)}
                className="p-1.5 rounded text-red-300 hover:bg-red-500/10 border border-transparent hover:border-red-500/20"
                title="Delete"
                data-testid={`media-delete-${f.id}`}
              >
                <Trash2 className="w-4 h-4" />
              </button>
            </div>
          );
        })}
      </div>
    </div>
  );
}

// Slim sidebar variant — one-line drop zone + dense file rows, theme-token colours.
function CompactMedia({ contextType, items, loading, uploading, progress, dragOver, setDragOver, onDrop, inputRef, handleFiles, togglePublic, remove }) {
  const iconBtn = { background: "none", border: "none", padding: 3, cursor: "pointer", color: "var(--t2-grey3)", display: "inline-flex" };
  return (
    <div data-testid={`media-manager-${contextType}`}>
      <div
        onDragOver={(e) => { e.preventDefault(); setDragOver(true); }}
        onDragLeave={() => setDragOver(false)}
        onDrop={onDrop}
        onClick={() => !uploading && inputRef.current?.click()}
        data-testid="media-dropzone"
        style={{ display: "flex", alignItems: "center", gap: 8, padding: "7px 10px", borderRadius: 8, cursor: "pointer", fontSize: 11.5,
          border: `1px dashed ${dragOver ? "var(--t2-orange)" : "var(--t2-border)"}`, color: "var(--t2-grey2)",
          background: dragOver ? "rgba(255,104,32,0.06)" : "transparent", transition: "border-color .15s, background-color .15s" }}
      >
        <input ref={inputRef} type="file" multiple className="hidden"
          onChange={(e) => { handleFiles(e.target.files); e.target.value = ""; }} data-testid="media-file-input" />
        {uploading
          ? <><Loader2 className="w-3.5 h-3.5 animate-spin" style={{ color: "var(--t2-orange-ink)" }} /> Uploading… {progress}%</>
          : <><UploadCloud className="w-3.5 h-3.5" style={{ color: "var(--t2-orange-ink)" }} /> Drop files or <span style={{ color: "var(--t2-orange-ink)", fontWeight: 700 }}>browse</span><span style={{ marginLeft: "auto", fontSize: 10, color: "var(--t2-grey4)" }}>20 MB max</span></>}
      </div>
      <div style={{ marginTop: 6, display: "flex", flexDirection: "column", gap: 2 }} data-testid="media-list">
        {loading && items.length === 0 && <div style={{ fontSize: 11, color: "var(--t2-grey3)", padding: "4px 2px" }}>Loading…</div>}
        {!loading && items.length === 0 && <div style={{ fontSize: 11, color: "var(--t2-grey3)", padding: "4px 2px" }} data-testid="media-empty">No files yet.</div>}
        {items.map((f) => {
          const Icon = iconFor(f.content_type);
          return (
            <div key={f.id} data-testid={`media-item-${f.id}`}
              style={{ display: "flex", alignItems: "center", gap: 6, padding: "4px 4px", borderBottom: "1px solid var(--t2-border)", fontSize: 11.5 }}>
              <Icon className="w-3.5 h-3.5" style={{ color: "var(--t2-grey3)", flexShrink: 0 }} />
              <a href={mediaUrl(f)} target="_blank" rel="noreferrer" title={`${f.filename} · ${humanSize(f.size)}`} data-testid={`media-open-${f.id}`}
                style={{ flex: 1, minWidth: 0, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap", color: "var(--t2-grey1)", textDecoration: "none" }}>{f.filename}</a>
              <button type="button" onClick={() => togglePublic(f)} style={{ ...iconBtn, color: f.is_public ? "var(--t2-teal-ink)" : "var(--t2-grey3)" }}
                title={f.is_public ? "Public — click to make private" : "Private — click to make public"} data-testid={`media-toggle-${f.id}`}>
                {f.is_public ? <Globe className="w-3.5 h-3.5" /> : <Lock className="w-3.5 h-3.5" />}
              </button>
              <a href={mediaUrl(f, { download: true })} style={iconBtn} title="Download" data-testid={`media-download-${f.id}`}><Download className="w-3.5 h-3.5" /></a>
              <button type="button" onClick={() => remove(f)} style={iconBtn} title="Delete" data-testid={`media-delete-${f.id}`}><Trash2 className="w-3.5 h-3.5" /></button>
            </div>
          );
        })}
      </div>
    </div>
  );
}

export default MediaManager;
